import React, { useState, useEffect, useMemo, useRef } from "react";
import { Pie, Column, DualAxes } from "@ant-design/plots";
import jsPDF from "jspdf";
import { toast } from "react-toastify";
import { Accident, Division, Site, AccidentHoursWorked } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useCompany, useSite, useLanguage } from "../../../../contexts";
import { LoadingSpinner, ExportPdfButton, ChartLegend } from "../../../Shared";
import { renderDonutForPdf, renderColumnForPdf, renderDualAxesForPdf, buildStandardDashboardPdf, captureElementAtWidth, prepareKpiCardsForPdf } from "../../../Shared/pdfCharts";

// Verdes del módulo de gestión (#95A472 es el color base de los encabezados)
const GREEN_DARK  = "#4A5530";
const GREEN_MAIN  = "#95A472";
const GREEN_MID   = "#7A8A4F";
const GREEN_LIGHT = "#B3C08F";
const GREEN_SHADES = ["#5E6B3C", "#95A472", "#B3C08F", "#7A8A4F", "#CCD6B0", "#4A5530"];
// Mismo patrón de barra mensual (colores + grid) que Requisitos/Plan Anual.
const GREEN_PALETTE = ["#7B9355", "#C4D693", "#A3B76B"];
const MONTH_BAR_STYLE = { radius: 5, maxWidth: 25, shadowColor: "transparent", shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0 };
const MONTH_BAR_SCALE_X = { padding: 0.08, paddingInner: 0.08, paddingOuter: 0.08 };
const MONTH_BAR_Y_AXIS = {
  labelFontFamily: "Archivo",
  labelFontWeight: 800,
  labelFontSize: 8,
  labelLineHeight: 8,
  labelFill: "#001936",
  labelOpacity: 1,
  gridStroke: "#D3D3D3",
  gridStrokeOpacity: 1,
  gridLineWidth: 1,
  gridLineDash: [5, 6],
};
const MONTH_BAR_X_AXIS = { labelFill: "#001936", labelOpacity: 1 };
const MONTH_BAR_INTERACTION = { tooltip: { crosshairs: false }, elementHighlightByX: false };
import { isAdmin, isMaster } from "../../../../utils/checkPermission";
import "./AccidentsDashboard.scss";

const CY = new Date().getFullYear();
const YEAR_OPTS = Array.from({ length: 5 }, (_, i) => CY - 2 + i);
const MONTH_KEYS = ["month_jan","month_feb","month_mar","month_apr","month_may","month_jun","month_jul","month_aug","month_sep","month_oct","month_nov","month_dec"];

// Chart 17 · Agrupaciones de partes del cuerpo según spec (claves internas en
// español porque así vienen guardadas en `bodyParts`; el label sí se traduce).
const BODY_GROUP_KEYS = {
  head_neck:   { key: "adb_body_head_neck",   parts: ["Cabeza", "Cara", "Ojos", "Cuello"] },
  trunk_back:  { key: "adb_body_trunk_back",  parts: ["Tronco/Espalda", "Hombros", "Caderas"] },
  upper_limb:  { key: "adb_body_upper_limb",  parts: ["Brazos", "Manos"] },
  lower_limb:  { key: "adb_body_lower_limb",  parts: ["Piernas", "Rodillas", "Tobillos", "Pies"] },
};

const accCtrl   = new Accident();
const divCtrl   = new Division();
const siteCtrl  = new Site();
const hoursCtrl = new AccidentHoursWorked();

const FilterDropdown = ({ options, value, onChange, placeholder, clearable = false }) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // El menú abre siempre hacia abajo — si el filtro queda cerca del borde
  // inferior de la pantalla, no entra y se corta contra el viewport. Si no
  // hay lugar debajo pero sí arriba, abre para arriba en su lugar.
  const toggleOpen = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const menuMaxHeight = 340;
      setOpenUp(window.innerHeight - rect.bottom < menuMaxHeight && rect.top > menuMaxHeight);
    }
    setOpen((v) => !v);
  };

  const selected = options.find((o) => String(o.value) === String(value));
  const hasValue = clearable && Boolean(value);

  return (
    <div className="adb-fd" ref={ref}>
      <button type="button" className="adb-fd-toggle" onClick={toggleOpen}>
        <span className={selected ? "" : "adb-fd-placeholder"}>{selected ? selected.text : placeholder}</span>
        {hasValue && (
          <span
            role="button"
            aria-label="clear"
            className="adb-fd-clear"
            onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
          >
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
              <path d="M2.5 2.5 9.5 9.5M9.5 2.5 2.5 9.5"/>
            </svg>
          </span>
        )}
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`adb-fd-arrow${open ? " adb-fd-arrow--open" : ""}`}>
          <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
        </svg>
      </button>
      {open && (
        <div className={`adb-fd-menu${openUp ? " adb-fd-menu--up" : ""}`}>
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`adb-fd-item${String(o.value) === String(value) ? " adb-fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

function applyPeriod(list, period, year) {
  const now = new Date();
  if (period === "year")    return list.filter(a => a.date && new Date(a.date).getFullYear() === year);
  if (period === "last12") {
    const cut = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    return list.filter(a => a.date && new Date(a.date) >= cut);
  }
  if (period === "quarter") {
    const q = Math.floor(now.getMonth() / 3);
    const s = new Date(now.getFullYear(), q * 3, 1);
    const e = new Date(now.getFullYear(), (q + 1) * 3, 0);
    return list.filter(a => { if (!a.date) return false; const d = new Date(a.date); return d >= s && d <= e; });
  }
  if (period === "month") {
    return list.filter(a => {
      if (!a.date) return false;
      const d = new Date(a.date);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    });
  }
  return list;
}

// Igual que applyPeriod, pero para registros {year, month} (AccidentHoursWorked)
// en vez de una fecha completa — sirve para sumar HH sobre el mismo rango que
// ya usa el dashboard para filtrar accidentes.
function applyPeriodHours(list, period, year) {
  const now = new Date();
  const toDate = (h) => new Date(h.year, h.month - 1, 1);
  if (period === "year")    return list.filter(h => h.year === year);
  if (period === "last12") {
    const cut = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    return list.filter(h => toDate(h) >= cut);
  }
  if (period === "quarter") {
    const q = Math.floor(now.getMonth() / 3);
    const s = new Date(now.getFullYear(), q * 3, 1);
    const e = new Date(now.getFullYear(), (q + 1) * 3, 0);
    return list.filter(h => { const d = toDate(h); return d >= s && d <= e; });
  }
  if (period === "month") {
    return list.filter(h => h.year === now.getFullYear() && h.month === now.getMonth() + 1);
  }
  return list;
}

// Cuadradito verde con flechita que acompaña los títulos de KPIs y gráficos
const GreenMark = () => (
  <span className="adb__sq" aria-hidden="true">
    <svg viewBox="0 0 10 10" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7 7 3M3.5 3H7v3.5" />
    </svg>
  </span>
);

// Descripciones del pie de la tarjeta (variación vs año anterior, "requiere HH",
// subtítulo) comentadas por diseño. sublabel/delta se siguen pasando desde
// el dashboard para poder reactivarlas sin tocar los cálculos.
function KpiCard({ label, value, na }) {
  return (
    <div className="adb__kpi">
      <div className="adb__kpi-label"><GreenMark />{label}</div>
      <div className="adb__kpi-value" style={{ color: na ? "#aaa" : undefined }}>{na ? "—" : value}</div>
      {/*
      <div className="adb__kpi-foot">
        {dn && (
          <span style={{ color: dColor, fontSize: "0.73rem", fontWeight: 600 }}>
            {delta > 0 ? "▲" : delta < 0 ? "▼" : "="} {Math.abs(delta)} {t("adb_vs_last_year")}
          </span>
        )}
        {na && <span className="adb__kpi-na">{t("adb_kpi_requires_data")}</span>}
        <span className="adb__kpi-sub">{sublabel}</span>
      </div>
      */}
    </div>
  );
}

function CBox({ title, children, empty, wide, ring }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  return (
    <div className={`adb__chart-cell${wide ? " adb__chart-cell--wide" : ""}`}>
      <div className="adb__chart-title"><GreenMark />{title}</div>
      <div className="adb__chart-box">
        {empty
          ? (
            <div className="adb__chart-empty">
              {ring && <div className="adb__empty-ring" aria-hidden="true" />}
              <span>{t("no_data")}</span>
            </div>
          )
          : <div className="adb__chart-inner">{children}</div>}
      </div>
    </div>
  );
}

const PIE_H      = 195;
const PIE_H_SM   = 155;
const BAR_H      = 210;

export function AccidentsDashboard({ onYearChange, onSearchChange, extYear, companyName, siteName }) {
  const { accessToken, user } = useAuth();
  const isAdminUser = isMaster(user?.role) || isAdmin(user?.role);
  const { company } = useCompany();
  const { site: contextSite } = useSite();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const PERIOD_OPTS = [
    { value: "year",    label: t("adb_period_year") },
    { value: "last12",  label: t("adb_period_last12") },
    { value: "quarter", label: t("adb_period_quarter") },
    { value: "month",   label: t("adb_period_month") },
  ];

  const [raw,       setRaw]       = useState(null);
  const [sites,     setSites]     = useState([]);
  const [divisions, setDivisions] = useState([]);
  const kpiRef = useRef();
  const [exportingPdf, setExportingPdf] = useState(false);
  const exportingRef = useRef(false);

  const [year,       setYear]       = useState(extYear ?? CY);
  const [siteId,     setSiteId]     = useState("");
  const [divisionId, setDivisionId] = useState("");
  const [period,     setPeriod]     = useState("year");
  const [search,     setSearch]     = useState("");

  // Horas trabajadas / dotación — panel admin-only, carga aislada (ver abajo)
  const [hoursRaw,       setHoursRaw]       = useState([]);
  const [showHoursPanel, setShowHoursPanel] = useState(false);
  const [savingHours,    setSavingHours]    = useState(false);
  const [newHours, setNewHours] = useState({
    site: "", year: CY, month: new Date().getMonth() + 1, hours: "", headcount: "",
  });
  const [showAllHoursHistory, setShowAllHoursHistory] = useState(false);

  const noSiteInCtx    = !contextSite || contextSite === "0" || contextSite === 0;
  const noCompanyInCtx = !company     || company     === "0" || company     === 0;

  useEffect(() => {
    (async () => {
      try {
        const [acc, divs, sts] = await Promise.all([
          accCtrl.getAccidents(accessToken, true),
          divCtrl.getDivisions(accessToken, true),
          // Sitios de la empresa activa (si hay una elegida) para el
          // dropdown local — no mezclar sitios de otras empresas del tenant.
          noCompanyInCtx
            ? siteCtrl.getSites(accessToken, true)
            : siteCtrl.getSitesByCompany(accessToken, company, true),
        ]);
        setRaw(Array.isArray(acc) ? acc : []);
        setDivisions(divs || []);
        setSites(sts || []);
      } catch { setRaw([]); }
    })();
  }, [accessToken, company, noCompanyInCtx]);

  // Fetch aislado a propósito: si esto falla (feature nueva, endpoint recién
  // desplegado, etc.) no debe afectar en nada al resto del dashboard, que ya
  // está en producción — por eso no va dentro del Promise.all de arriba.
  useEffect(() => {
    hoursCtrl.getAccidentHoursWorked(accessToken)
      .then((res) => setHoursRaw(Array.isArray(res) ? res : []))
      .catch(() => setHoursRaw([]));
  }, [accessToken]);

  useEffect(() => {
    if (extYear !== undefined && extYear !== year) setYear(extYear);
  }, [extYear]);

  const handleYear   = (y) => { setYear(y);   onYearChange?.(y); };
  const handleSearch = (s) => { setSearch(s); onSearchChange?.(s); };

  // Sitios de la empresa activa, para acotar cuando no hay un sitio puntual
  // elegido en el header (evita mezclar accidentes de otras empresas).
  const companySiteIds = useMemo(() => sites.map((s) => s._id), [sites]);

  const data = useMemo(() => {
    if (!raw) return [];
    let d = applyPeriod([...raw], period, year);
    if (!noSiteInCtx) {
      d = d.filter(a => (a.site?._id || a.site) === contextSite);
    } else if (!noCompanyInCtx) {
      d = d.filter(a => companySiteIds.includes(a.site?._id || a.site));
    }
    if (siteId)     d = d.filter(a => (a.site?._id     || a.site)     === siteId);
    if (divisionId) d = d.filter(a => (a.division?._id || a.division) === divisionId);
    return d;
  }, [raw, year, period, siteId, divisionId, contextSite, noSiteInCtx, noCompanyInCtx, companySiteIds]);

  const prev = useMemo(() => {
    if (!raw) return [];
    let d = raw.filter(a => a.date && new Date(a.date).getFullYear() === year - 1);
    if (!noSiteInCtx) {
      d = d.filter(a => (a.site?._id || a.site) === contextSite);
    } else if (!noCompanyInCtx) {
      d = d.filter(a => companySiteIds.includes(a.site?._id || a.site));
    }
    if (siteId)     d = d.filter(a => (a.site?._id     || a.site)     === siteId);
    if (divisionId) d = d.filter(a => (a.division?._id || a.division) === divisionId);
    return d;
  }, [raw, year, siteId, divisionId, contextSite, noSiteInCtx, noCompanyInCtx, companySiteIds]);

  // Horas trabajadas en el mismo rango filtrado que `data`/`prev` — sin
  // aplicar `divisionId` porque las horas son por sitio, no por división.
  const hoursData = useMemo(() => {
    let h = applyPeriodHours(hoursRaw, period, year);
    if (!noSiteInCtx) {
      h = h.filter(x => (x.site?._id || x.site) === contextSite);
    } else if (!noCompanyInCtx) {
      h = h.filter(x => companySiteIds.includes(x.site?._id || x.site));
    }
    if (siteId) h = h.filter(x => (x.site?._id || x.site) === siteId);
    return h;
  }, [hoursRaw, year, period, siteId, contextSite, noSiteInCtx, noCompanyInCtx, companySiteIds]);

  const hoursPrev = useMemo(() => {
    let h = hoursRaw.filter(x => x.year === year - 1);
    if (!noSiteInCtx) {
      h = h.filter(x => (x.site?._id || x.site) === contextSite);
    } else if (!noCompanyInCtx) {
      h = h.filter(x => companySiteIds.includes(x.site?._id || x.site));
    }
    if (siteId) h = h.filter(x => (x.site?._id || x.site) === siteId);
    return h;
  }, [hoursRaw, year, siteId, contextSite, noSiteInCtx, noCompanyInCtx, companySiteIds]);

  // Tabla del panel de carga — acotada al sitio activo (para no mezclar
  // todos los sitios en una sola lista que crece sin parar) y, por defecto,
  // a los últimos 12 meses (con opción de ver el historial completo).
  const hoursTableSiteId = !noSiteInCtx ? contextSite : (siteId || null);
  const hoursTableSiteName = hoursTableSiteId
    ? (siteName || sites.find(s => s._id === hoursTableSiteId)?.name || "")
    : null;
  const hoursTableRows = useMemo(() => {
    let rows = hoursTableSiteId
      ? hoursRaw.filter(h => (h.site?._id || h.site) === hoursTableSiteId)
      : hoursRaw;
    if (!showAllHoursHistory) rows = applyPeriodHours(rows, "last12", CY);
    return [...rows].sort((a, b) => (b.year - a.year) || (b.month - a.month));
  }, [hoursRaw, hoursTableSiteId, showAllHoursHistory]);

  // Recordatorio — solo si el sitio actualmente seleccionado no tiene
  // absolutamente ninguna hora cargada (nunca se cargó nada para ese sitio).
  const selectedSiteHasNoHours = hoursTableSiteId
    ? !hoursRaw.some(h => (h.site?._id || h.site) === hoursTableSiteId)
    : false;

  // ── KPIs ──────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const tot  = data.length,  ptot  = prev.length;
    const days = data.reduce((s, a) => s + (a.lostDays || 0), 0);
    const pdays= prev.reduce((s, a) => s + (a.lostDays || 0), 0);
    const wBaja= data.filter(a => (a.lostDays || 0) > 0);
    const pwBaja=prev.filter(a => (a.lostDays || 0) > 0);
    const dur  = wBaja.length  ? +(days  / wBaja.length).toFixed(1)  : 0;
    const pdur = pwBaja.length ? +(pdays / pwBaja.length).toFixed(1) : 0;
    const isClosed = (a) => a.caseStatus === "closed" || a.caseStatus === "Cerrado";
    const open = data.filter(a => !isClosed(a)).length;
    const popen= prev.filter(a => !isClosed(a)).length;

    // Índices de Frecuencia / Gravedad / Incidencia — requieren HH (y
    // dotación, para incidencia) cargadas para el sitio/período filtrado.
    // Sin datos cargados quedan en null → las tarjetas siguen mostrando "—"
    // exactamente como antes de esta feature.
    const totalHours     = hoursData.reduce((s, h) => s + (h.hours || 0), 0);
    const totalHoursPrev = hoursPrev.reduce((s, h) => s + (h.hours || 0), 0);
    const hcVals     = hoursData.filter(h => h.headcount != null).map(h => h.headcount);
    const hcValsPrev = hoursPrev.filter(h => h.headcount != null).map(h => h.headcount);
    const avgHeadcount     = hcVals.length     ? hcVals.reduce((a, b) => a + b, 0) / hcVals.length         : 0;
    const avgHeadcountPrev = hcValsPrev.length ? hcValsPrev.reduce((a, b) => a + b, 0) / hcValsPrev.length : 0;

    const freqIdx  = totalHours     ? Math.round(wBaja.length  * 1000000 / totalHours)     : null;
    const freqIdxP = totalHoursPrev ? Math.round(pwBaja.length * 1000000 / totalHoursPrev) : null;
    const sevIdx   = totalHours     ? Math.round(days  * 1000000 / totalHours)     : null;
    const sevIdxP  = totalHoursPrev ? Math.round(pdays * 1000000 / totalHoursPrev) : null;
    const incIdx   = avgHeadcount     ? +(tot  * 1000 / avgHeadcount).toFixed(1)     : null;
    const incIdxP  = avgHeadcountPrev ? +(ptot * 1000 / avgHeadcountPrev).toFixed(1) : null;

    return {
      total:   { val: tot,  delta: tot - ptot,                sub: t("adb_kpi_sub_total") },
      days:    { val: days, delta: days - pdays,              sub: t("adb_kpi_sub_ytd") },
      dur:     { val: dur,  delta: +(dur - pdur).toFixed(1),  sub: t("adb_kpi_sub_duration") },
      open:    { val: open, delta: open - popen,              sub: t("adb_kpi_sub_open") },
      freq: { val: freqIdx, na: freqIdx === null, delta: (freqIdx !== null && freqIdxP !== null) ? freqIdx - freqIdxP : null, sub: t("adb_kpi_sub_frequency") },
      sev:  { val: sevIdx,  na: sevIdx  === null, delta: (sevIdx  !== null && sevIdxP  !== null) ? sevIdx  - sevIdxP  : null, sub: t("adb_kpi_sub_severity") },
      inc:  { val: incIdx,  na: incIdx  === null, delta: (incIdx  !== null && incIdxP  !== null) ? +(incIdx - incIdxP).toFixed(1) : null, sub: t("adb_kpi_sub_incidence") },
    };
  }, [data, prev, hoursData, hoursPrev, translations]);

  // ── Horas trabajadas — alta / baja ─────────────────────────────
  const handleAddHours = async () => {
    if (!newHours.site || !newHours.year || !newHours.month || newHours.hours === "") {
      toast.warning(t("adb_hours_missing_fields"));
      return;
    }
    setSavingHours(true);
    try {
      const saved = await hoursCtrl.upsertAccidentHoursWorked(accessToken, {
        site: newHours.site,
        year: Number(newHours.year),
        month: Number(newHours.month),
        hours: Number(newHours.hours),
        ...(newHours.headcount !== "" ? { headcount: Number(newHours.headcount) } : {}),
      });
      setHoursRaw((prevRaw) => {
        const idx = prevRaw.findIndex((h) => h._id === saved._id);
        if (idx >= 0) { const next = [...prevRaw]; next[idx] = saved; return next; }
        return [saved, ...prevRaw];
      });
      toast.success(t("adb_hours_saved"));
      setNewHours({ site: "", year: CY, month: new Date().getMonth() + 1, hours: "", headcount: "" });
    } catch (err) {
      toast.error(err?.msg || t("adb_hours_save_error"));
    } finally {
      setSavingHours(false);
    }
  };

  const handleDeleteHours = async (id) => {
    try {
      await hoursCtrl.deleteAccidentHoursWorked(accessToken, id);
      setHoursRaw((prevRaw) => prevRaw.filter((h) => h._id !== id));
    } catch {
      toast.error(t("adb_hours_save_error"));
    }
  };

  // ── Chart 13 · Accidentes por mes ──────────────────────────────
  const monthlyBars = useMemo(() => {
    const c = Array(12).fill(0);
    data.forEach(a => { if (a.date) { const m = new Date(a.date).getMonth(); c[m]++; } });
    return MONTH_KEYS.map((key, i) => ({ mes: t(key), value: c[i] }));
  }, [data, translations]);

  // ── Chart 14 · Por clasificación ─────────────────────────────
  const classData = useMemo(() => {
    const c = {};
    data.forEach(a => { const k = a.classification ? t(a.classification) : t("adb_no_classification"); c[k] = (c[k]||0)+1; });
    return Object.entries(c).map(([type, value]) => ({ type, value }));
  }, [data, translations]);

  // ── Chart 15 · Por tipo de caso ──────────────────────────────
  const ctypeData = useMemo(() => {
    const c = {};
    data.forEach(a => { const k = a.caseType ? t(a.caseType) : t("adb_no_case_type"); c[k] = (c[k]||0)+1; });
    return Object.entries(c).map(([type, value]) => ({ type, value }));
  }, [data, translations]);

  // ── Chart 16 · Por forma del accidente ───────────────────────
  const formaData = useMemo(() => {
    const c = {};
    data.forEach(a => { const k = a.accidentType?.name || t("adb_no_form"); c[k] = (c[k]||0)+1; });
    return Object.entries(c).sort((a,b) => b[1]-a[1]).slice(0,6).map(([tipo,value]) => ({ tipo, value }));
  }, [data, translations]);

  // ── Chart 17 · Por parte del cuerpo (agrupado) ───────────────
  const bodyData = useMemo(() => {
    const rawCount = {};
    data.forEach(a => (a.bodyParts || []).forEach(bp => { rawCount[bp] = (rawCount[bp] || 0) + 1; }));
    const grouped = {};
    for (const { key, parts } of Object.values(BODY_GROUP_KEYS)) {
      const total = parts.reduce((s, p) => s + (rawCount[p] || 0), 0);
      if (total > 0) grouped[t(key)] = total;
    }
    const alreadyGrouped = Object.values(BODY_GROUP_KEYS).flatMap((g) => g.parts);
    for (const [bp, cnt] of Object.entries(rawCount)) {
      if (!alreadyGrouped.includes(bp)) grouped[bp] = (grouped[bp] || 0) + cnt;
    }
    return Object.entries(grouped).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([parte, value]) => ({ parte, value }));
  }, [data, translations]);

  // ── Chart 18 · Por causa principal ───────────────────────────
  const causaData = useMemo(() => {
    const c = {};
    data.forEach(a => (a.rootCause?.causes||[]).forEach(ca => {
      const k = t(ca);
      c[k] = (c[k]||0)+1;
    }));
    return Object.entries(c).filter(([,v])=>v>0).map(([type,value]) => ({ type, value }));
  }, [data, translations]);

  // ── Chart 19 · Por sitio — barras (cantidad) + línea (% con baja) ─
  const sitioComboData = useMemo(() => {
    const c = {};
    data.forEach(a => {
      const k = a.site?.name || t("adb_no_site");
      if (!c[k]) c[k] = { sitio: k, cantidad: 0, conBaja: 0 };
      c[k].cantidad++;
      if ((a.lostDays || 0) > 0) c[k].conBaja++;
    });
    return Object.values(c)
      .sort((a, b) => b.cantidad - a.cantidad)
      .map(d => ({ ...d, tasaBaja: d.cantidad ? +(d.conBaja / d.cantidad * 100).toFixed(1) : 0 }));
  }, [data, translations]);

  // ── Chart 20 · Top puestos ────────────────────────────────────
  const puestoData = useMemo(() => {
    const c = {};
    data.forEach(a => { if (a.occupation) c[a.occupation] = (c[a.occupation]||0)+1; });
    return Object.entries(c).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([puesto,value]) => ({ puesto, value }));
  }, [data]);

  // ── Chart 21 · Plan de acción por mes ────────────────────────
  const planData = useMemo(() => {
    const m = {};
    MONTH_KEYS.forEach(key => { m[key] = { closed:0, in_progress:0, overdue:0 }; });
    data.forEach(a => (a.correctiveActions||[]).forEach(ca => {
      if (!ca.dueDate) return;
      const k = MONTH_KEYS[new Date(ca.dueDate).getMonth()];
      if (!m[k]) return;
      if (ca.status==="closed")        m[k].closed++;
      else if (ca.status==="overdue")  m[k].overdue++;
      else                             m[k].in_progress++;
    }));
    return MONTH_KEYS.flatMap(key => [
      { mes: t(key), estado: t("closed"),      value: m[key].closed },
      { mes: t(key), estado: t("in_progress"), value: m[key].in_progress },
      { mes: t(key), estado: t("overdue"),     value: m[key].overdue },
    ]);
  }, [data, translations]);

  // Con pocos registros el máximo apilado puede ser 1 o 2 — el algoritmo
  // "nice" de G2 reparte igual 5 ticks y termina mostrando decimales
  // (0, 0.2, 0.4…) para un conteo. Con el máximo real fijamos tickCount
  // para que el paso entre ticks sea siempre un entero.
  const planYScale = useMemo(() => {
    const sums = {};
    planData.forEach(d => { sums[d.mes] = (sums[d.mes] || 0) + d.value; });
    const max = Math.max(0, ...Object.values(sums));
    return max > 0 && max <= 5 ? { nice: true, tickCount: max + 1 } : { nice: true };
  }, [planData]);

  // ── Chart 22 · Eficacia + % cumplimiento en plazo ─────────────
  const eficaciaData = useMemo(() => {
    const c = { effective:0, pending:0, ineffective:0 };
    data.forEach(a => (a.correctiveActions||[]).filter(ca=>ca.status==="closed").forEach(ca => {
      if (ca.efficacy==="effective")       c.effective++;
      else if (ca.efficacy==="ineffective") c.ineffective++;
      else                                 c.pending++;
    }));
    return Object.entries(c).filter(([,v])=>v>0).map(([key,value]) => ({ type: t(key), value }));
  }, [data, translations]);

  const cumplimientoStats = useMemo(() => {
    let cerradas = 0, vencidas = 0;
    data.forEach(a => (a.correctiveActions || []).forEach(ca => {
      if (ca.status === "closed")       cerradas++;
      else if (ca.status === "overdue") vencidas++;
    }));
    const total = cerradas + vencidas;
    return { pct: total > 0 ? Math.round(cerradas / total * 100) : null };
  }, [data]);

  if (raw === null) return <LoadingSpinner />;

  const pieConf = (d, colors, h) => ({
    data: d, angleField: "value", colorField: "type",
    innerRadius: 0.62,
    scale: { color: { range: colors || GREEN_SHADES } },
    label: false, height: h || PIE_H,
    legend: { position: "bottom", maxRows: 3, layout: { justifyContent: "center" } },
    tooltip: { title: "type", items: [{ field: "value", name: t("quantity") }] },
  });

  const pctColor = (pct) => pct >= 75 ? "#059669" : pct >= 50 ? "#D97706" : "#DC2626";

  const handleExportPdf = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setExportingPdf(true);
    const chartCleanups = [];
    try {
      await document.fonts.ready;
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const classColors = GREEN_SHADES;
      const classColorMap = Object.fromEntries(classData.map((d, i) => [d.type, classColors[i % classColors.length]]));
      const ctypeColors = GREEN_SHADES;
      const ctypeColorMap = Object.fromEntries(ctypeData.map((d, i) => [d.type, ctypeColors[i % ctypeColors.length]]));
      const causaColors = GREEN_SHADES;
      const causaColorMap = Object.fromEntries(causaData.map((d, i) => [d.type, causaColors[i % causaColors.length]]));
      const eficaciaColors = ["#059669", "#F59E0B", "#DC2626"];
      const eficaciaColorMap = Object.fromEntries(eficaciaData.map((d, i) => [d.type, eficaciaColors[i % eficaciaColors.length]]));

      const [
        monthlyColumn, classDonut, ctypeDonut,
        formaBar, bodyBar, causaDonut,
        sitioDual, puestoBar, planColumn, eficaciaDonut,
      ] = await Promise.all([
        renderColumnForPdf(monthlyBars, { xField: "mes", fillColor: GREEN_MAIN }),
        renderDonutForPdf(classData, classColorMap, { showPercentLabels: true }),
        renderDonutForPdf(ctypeData, ctypeColorMap, { showPercentLabels: true }),
        renderColumnForPdf(formaData, { xField: "tipo", fillColor: GREEN_MAIN, radius: 4 }),
        renderColumnForPdf(bodyData, { xField: "parte", fillColor: GREEN_MID, radius: 4 }),
        renderDonutForPdf(causaData, causaColorMap, { showPercentLabels: true }),
        renderDualAxesForPdf(sitioComboData, [
          { type: "interval", yField: "cantidad", style: { fill: GREEN_MAIN }, axis: { y: { title: t("accidents"), position: "left" } } },
          { type: "line", yField: "tasaBaja", style: { stroke: GREEN_DARK, lineWidth: 2 }, point: { shapeField: "circle", sizeField: 4 }, axis: { y: { title: t("adb_leave_rate_pct"), position: "right" } } },
        ], { xField: "sitio" }),
        renderColumnForPdf(puestoData, { xField: "puesto", fillColor: GREEN_LIGHT, radius: 4 }),
        renderColumnForPdf(planData, { xField: "mes", colorField: "estado", stack: true, colorMap: { [t("closed")]: GREEN_PALETTE[0], [t("in_progress")]: GREEN_PALETTE[1], [t("overdue")]: "#DC2626" } }),
        renderDonutForPdf(eficaciaData, eficaciaColorMap, { showPercentLabels: true }),
      ]);
      [monthlyColumn, classDonut, ctypeDonut, formaBar, bodyBar, causaDonut, sitioDual, puestoBar, planColumn, eficaciaDonut]
        .forEach((r) => { if (r.cleanup) chartCleanups.push(r.cleanup); });

      const kpiCanvas = await captureElementAtWidth(kpiRef.current, 1400, {}, prepareKpiCardsForPdf);

      const eficaciaTitle = cumplimientoStats.pct !== null
        ? `${t("adb_action_efficacy")} (${cumplimientoStats.pct}% ${t("adb_on_time_compliance")})`
        : t("adb_action_efficacy");

      await buildStandardDashboardPdf({
        jsPDFCtor: jsPDF,
        pageTitle: `${t("accident_management")} — ${year}`,
        companyName: [companyName, siteName].filter(Boolean).join(" · "),
        generatedOnLabel: t("generated_on"),
        kpiCanvas,
        chartRows: [
          { height: 210, cards: [
            { title: t("adb_chart_by_month"), ...monthlyColumn },
            { title: t("adb_chart_by_classification"), ...classDonut, legendPosition: "right" },
            { title: t("adb_chart_by_case_type"), ...ctypeDonut, legendPosition: "right" },
          ] },
          { height: 210, cards: [
            { title: t("adb_chart_by_form"), ...formaBar },
            { title: t("adb_chart_by_body_part"), ...bodyBar },
            { title: t("adb_chart_by_cause"), ...causaDonut, legendPosition: "right" },
          ] },
          { height: 260, section: { title: t("adb_chart_by_site"), ...sitioDual } },
          { height: 210, cards: [
            { title: t("adb_chart_top_positions"), ...puestoBar },
            { title: t("adb_chart_action_plan_by_month"), ...planColumn },
            { title: eficaciaTitle, ...eficaciaDonut, legendPosition: "right" },
          ] },
        ],
        filename: `accidentes_${year}.pdf`,
      });
    } catch (err) {
      console.error("Error exportando PDF:", err);
    } finally {
      chartCleanups.forEach((fn) => fn());
      exportingRef.current = false;
      setExportingPdf(false);
    }
  };

  return (
    <div className="adb">

      {/* ── FILTROS ─────────────────────────────────────────── */}
      <div className="adb__filters">
        <div className="adb__search">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input className="adb__search-input" placeholder={`${t("search")}...`} value={search} onChange={e => handleSearch(e.target.value)} />
        </div>
        <div className="adb__fb">
          <FilterDropdown
            options={YEAR_OPTS.map(y => ({ key: y, value: y, text: String(y) }))}
            value={year}
            onChange={(v) => handleYear(Number(v))}
            placeholder={t("year")}
          />
        </div>
        <div className="adb__fb">
          <FilterDropdown
            options={sites.map(s => ({ key: s._id, value: s._id, text: s.name }))}
            value={siteId}
            onChange={setSiteId}
            placeholder={t("site")}
            clearable
          />
        </div>
        <div className="adb__fb">
          <FilterDropdown
            options={divisions.map(d => ({ key: d._id, value: d._id, text: d.name }))}
            value={divisionId}
            onChange={setDivisionId}
            placeholder={t("division")}
            clearable
          />
        </div>
        <div className="adb__fb">
          <FilterDropdown
            options={PERIOD_OPTS.map(o => ({ key: o.value, value: o.value, text: o.label }))}
            value={period}
            onChange={setPeriod}
            placeholder={t("period")}
          />
        </div>
        <div className="adb__fb" style={{ flex: "0 0 auto", minWidth: "auto" }}>
          <ExportPdfButton onClick={handleExportPdf} loading={exportingPdf} title={t("export_pdf")} />
        </div>
      </div>

      {/* ── KPIs ────────────────────────────────────────────── */}
      <div className="adb__kpis" ref={kpiRef}>
        <KpiCard label={t("accidents")}                  sublabel={kpis.total.sub} value={kpis.total.val}  delta={kpis.total.delta}  />
        <KpiCard label={t("lost_days")}                   sublabel={kpis.days.sub}  value={kpis.days.val}   delta={kpis.days.delta}   />
        <KpiCard label={t("adb_kpi_frequency_index")}     sublabel={kpis.freq.sub} value={kpis.freq.val ?? "—"} delta={kpis.freq.delta} na={kpis.freq.na} />
        <KpiCard label={t("adb_kpi_severity_index")}      sublabel={kpis.sev.sub}  value={kpis.sev.val ?? "—"}  delta={kpis.sev.delta}  na={kpis.sev.na} />
        <KpiCard label={t("adb_kpi_avg_duration")}        sublabel={kpis.dur.sub}   value={kpis.dur.val}    delta={kpis.dur.delta}    />
        <KpiCard label={t("adb_kpi_incidence_index")}     sublabel={kpis.inc.sub} value={kpis.inc.val ?? "—"} delta={kpis.inc.delta} na={kpis.inc.na} />
        <KpiCard label={t("adb_kpi_open_cases")}          sublabel={kpis.open.sub}  value={kpis.open.val}   delta={kpis.open.delta}   />
      </div>

      {/* ── Horas trabajadas / dotación (admin-only, colapsado) ── */}
      {isAdminUser && (
        <div className="adb__hours">
          {selectedSiteHasNoHours && (
            <button type="button" className="adb__hours-reminder" onClick={() => setShowHoursPanel(true)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              {t("adb_hours_reminder_prefix")} {hoursTableSiteName}
            </button>
          )}
          <button type="button" className="adb__hours-toggle" onClick={() => setShowHoursPanel(v => !v)}>
            {t("adb_hours_panel_toggle")}
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`adb__hours-arrow${showHoursPanel ? " adb__hours-arrow--open" : ""}`}>
              <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
            </svg>
          </button>

          {showHoursPanel && (
            <div className="adb__hours-panel">
              <div className="adb__hours-title">{t("adb_hours_panel_title")}</div>
              <div className="adb__hours-form">
                <select className="adb__sel" value={newHours.site} onChange={e => setNewHours(p => ({ ...p, site: e.target.value }))}>
                  <option value="">{t("adb_hours_col_site")}...</option>
                  {sites.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
                <select className="adb__sel" value={newHours.year} onChange={e => setNewHours(p => ({ ...p, year: Number(e.target.value) }))}>
                  {YEAR_OPTS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select className="adb__sel" value={newHours.month} onChange={e => setNewHours(p => ({ ...p, month: Number(e.target.value) }))}>
                  {MONTH_KEYS.map((key, i) => <option key={key} value={i + 1}>{t(key)}</option>)}
                </select>
                <input
                  type="number" min="0" className="adb__search-input" style={{ maxWidth: 110 }}
                  placeholder={t("adb_hours_col_hours")}
                  value={newHours.hours}
                  onChange={e => setNewHours(p => ({ ...p, hours: e.target.value }))}
                />
                <input
                  type="number" min="0" className="adb__search-input" style={{ maxWidth: 150 }}
                  placeholder={t("adb_hours_headcount_optional")}
                  value={newHours.headcount}
                  onChange={e => setNewHours(p => ({ ...p, headcount: e.target.value }))}
                />
                <button type="button" className="adb__hours-add-btn" disabled={savingHours} onClick={handleAddHours}>
                  {t("adb_hours_add")}
                </button>
              </div>

              <div className="adb__hours-scope">
                <span>
                  {hoursTableSiteName
                    ? `${hoursTableSiteName} · ${showAllHoursHistory ? t("adb_hours_scope_all") : t("adb_hours_scope_last12")}`
                    : `${t("adb_hours_scope_all_sites")} · ${showAllHoursHistory ? t("adb_hours_scope_all") : t("adb_hours_scope_last12")}`}
                </span>
                <button type="button" className="adb__hours-scope-toggle" onClick={() => setShowAllHoursHistory(v => !v)}>
                  {showAllHoursHistory ? t("adb_hours_show_recent") : t("adb_hours_show_all")}
                </button>
              </div>

              {hoursTableRows.length === 0 ? (
                <div className="adb__hours-empty">{t("adb_hours_empty")}</div>
              ) : (
                <div className="adb__hours-table-wrap">
                  <table className="adb__hours-table">
                    <thead>
                      <tr>
                        {!hoursTableSiteId && <th>{t("adb_hours_col_site")}</th>}
                        <th>{t("adb_hours_col_year")}</th>
                        <th>{t("adb_hours_col_month")}</th>
                        <th>{t("adb_hours_col_hours")}</th>
                        <th>{t("adb_hours_col_headcount")}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {hoursTableRows.map(h => (
                        <tr key={h._id}>
                          {!hoursTableSiteId && <td>{h.site?.name || "-"}</td>}
                          <td>{h.year}</td>
                          <td>{t(MONTH_KEYS[h.month - 1])}</td>
                          <td>{h.hours}</td>
                          <td>{h.headcount ?? "-"}</td>
                          <td>
                            <button type="button" className="adb__hours-del-btn" onClick={() => handleDeleteHours(h._id)}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── GRÁFICOS ─────────────────────────────────────────── */}
      <div className="adb__charts">

        {/* 13 · Accidentes por mes */}
        <CBox title={t("adb_chart_by_month")} empty={!data.length}>
          <Column data={monthlyBars} xField="mes" yField="value" colorField="mes"
            height={BAR_H}
            scale={{ color: { range: GREEN_PALETTE }, x: MONTH_BAR_SCALE_X }}
            legend={false}
            style={MONTH_BAR_STYLE}
            axis={{ x: MONTH_BAR_X_AXIS, y: MONTH_BAR_Y_AXIS }}
            tooltip={{ items: [{ channel: "y", name: t("quantity") }] }}
            interaction={MONTH_BAR_INTERACTION}
          />
        </CBox>

        {/* 14 · Por clasificación */}
        <CBox title={t("adb_chart_by_classification")} empty={!classData.length} ring>
          <div className="adb__pie-row">
            <div className="chartWrapper adb__pie-wrapper">
              <Pie {...pieConf(classData, GREEN_SHADES)} legend={false} />
            </div>
            <ChartLegend items={classData.map((d, i) => ({ label: d.type, color: GREEN_SHADES[i % GREEN_SHADES.length] }))} />
          </div>
        </CBox>

        {/* 15 · Por tipo de caso */}
        <CBox title={t("adb_chart_by_case_type")} empty={!ctypeData.length} ring>
          <div className="adb__pie-row">
            <div className="chartWrapper adb__pie-wrapper">
              <Pie {...pieConf(ctypeData, GREEN_SHADES)} legend={false} />
            </div>
            <ChartLegend items={ctypeData.map((d, i) => ({ label: d.type, color: GREEN_SHADES[i % GREEN_SHADES.length] }))} />
          </div>
        </CBox>

        {/* 16 · Por forma del accidente */}
        <CBox title={t("adb_chart_by_form")} empty={!formaData.length}>
          <Column data={formaData} xField="tipo" yField="value" colorField="tipo" height={BAR_H}
            scale={{ color: { range: GREEN_PALETTE }, x: MONTH_BAR_SCALE_X }}
            legend={false}
            style={MONTH_BAR_STYLE}
            axis={{ x: { ...MONTH_BAR_X_AXIS, labelAutoEllipsis: true, labelAutoHide: true }, y: MONTH_BAR_Y_AXIS }}
            tooltip={{ items: [{ channel: "y", name: t("quantity") }] }}
            interaction={MONTH_BAR_INTERACTION}
          />
        </CBox>

        {/* 17 · Por parte del cuerpo (agrupado) */}
        <CBox title={t("adb_chart_by_body_part")} empty={!bodyData.length}>
          <Column data={bodyData} xField="parte" yField="value" colorField="parte" height={BAR_H}
            scale={{ color: { range: GREEN_PALETTE }, x: MONTH_BAR_SCALE_X }}
            legend={false}
            style={MONTH_BAR_STYLE}
            axis={{ x: { ...MONTH_BAR_X_AXIS, labelAutoEllipsis: true, labelAutoHide: true }, y: MONTH_BAR_Y_AXIS }}
            tooltip={{ items: [{ channel: "y", name: t("quantity") }] }}
            interaction={MONTH_BAR_INTERACTION}
          />
        </CBox>

        {/* 18 · Por causa principal */}
        <CBox title={t("adb_chart_by_cause")} empty={!causaData.length} ring>
          <div className="adb__pie-row">
            <div className="chartWrapper adb__pie-wrapper">
              <Pie {...pieConf(causaData, GREEN_SHADES)} legend={false} />
            </div>
            <ChartLegend items={causaData.map((d, i) => ({ label: d.type, color: GREEN_SHADES[i % GREEN_SHADES.length] }))} />
          </div>
        </CBox>

        {/* 19 · Por sitio — cantidad (barra) + % con baja (línea) */}
        <CBox title={t("adb_chart_by_site")} empty={!sitioComboData.length}>
          <DualAxes
            data={sitioComboData}
            xField="sitio"
            height={BAR_H}
            axis={{ x: MONTH_BAR_X_AXIS }}
            children={[
              {
                type: "interval",
                yField: "cantidad",
                style: { fill: GREEN_MAIN, ...MONTH_BAR_STYLE },
                scale: { x: MONTH_BAR_SCALE_X },
                axis: { y: { ...MONTH_BAR_Y_AXIS, title: t("accidents"), position: "left" } },
                tooltip: { items: [{ field: "cantidad", name: t("accidents") }] },
              },
              {
                type: "line",
                yField: "tasaBaja",
                style: { stroke: GREEN_DARK, lineWidth: 2 },
                point: { shapeField: "circle", sizeField: 4 },
                axis: { y: { title: t("adb_leave_rate_pct"), position: "right" } },
                tooltip: { items: [{ field: "tasaBaja", name: t("adb_leave_rate_pct") }] },
              },
            ]}
          />
        </CBox>

        {/* 20 · Top puestos */}
        <CBox title={t("adb_chart_top_positions")} empty={!puestoData.length}>
          <Column data={puestoData} xField="puesto" yField="value" colorField="puesto" height={BAR_H}
            scale={{ color: { range: GREEN_PALETTE }, x: MONTH_BAR_SCALE_X }}
            legend={false}
            style={MONTH_BAR_STYLE}
            axis={{ x: { ...MONTH_BAR_X_AXIS, labelAutoEllipsis: true, labelAutoHide: true }, y: MONTH_BAR_Y_AXIS }}
            tooltip={{ items: [{ channel: "y", name: t("quantity") }] }}
            interaction={MONTH_BAR_INTERACTION}
          />
        </CBox>

        {/* 21 + 22 · Plan de acción + Eficacia — fila completa */}
        <CBox title={t("adb_chart_action_plan_by_month")}>
          <div className="adb__plan-row">
            <div className="adb__plan-bars">
              <Column data={planData} xField="mes" yField="value" colorField="estado"
                stack height={BAR_H}
                scale={{ color: { range: [GREEN_PALETTE[0], GREEN_PALETTE[1], "#DC2626"] }, x: MONTH_BAR_SCALE_X, y: planYScale }}
                legend={{ position: "bottom" }}
                style={MONTH_BAR_STYLE}
                axis={{ x: MONTH_BAR_X_AXIS, y: MONTH_BAR_Y_AXIS }}
                tooltip={{ items: [{ channel: "y" }] }}
                interaction={MONTH_BAR_INTERACTION}
              />
            </div>
            <div className="adb__plan-eficacia">
              <div className="adb__plan-pct-wrap">
                <span
                  className="adb__plan-pct"
                  style={{ color: cumplimientoStats.pct !== null ? pctColor(cumplimientoStats.pct) : "#D1D5DB" }}
                >
                  {cumplimientoStats.pct !== null ? `${cumplimientoStats.pct}%` : "—"}
                </span>
                <span className="adb__plan-pct-label">{t("adb_on_time_compliance")}</span>
              </div>
              {eficaciaData.length > 0
                ? <Pie {...pieConf(eficaciaData, ["#059669","#F59E0B","#DC2626"], PIE_H_SM)} />
                : <div className="adb__chart-empty" style={{ height: 60, minHeight: 0 }}>{t("adb_no_closed_actions")}</div>
              }
              <span className="adb__plan-eficacia-label">{t("adb_action_efficacy")}</span>
            </div>
          </div>
        </CBox>

      </div>
    </div>
  );
}
